import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { Search, Star, MapPin } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface Nutritionist {
  id: string;
  name: string;
  bio: string | null;
  profile_image_url: string | null;
  rating_average: number;
  total_reviews: number;
  service_areas: string[] | null;
}

export default function NutritionistDiscoveryPage() {
  const [nutritionists, setNutritionists] = useState<Nutritionist[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadNutritionists();
  }, []);

  const loadNutritionists = async () => {
    try {
      const { data, error } = await supabase
        .from('nutritionists')
        .select('*')
        .eq('is_active', true)
        .eq('verification_status', 'verified');

      if (error) throw error;

      // Sort by rating
      const sorted = (data || []).sort(
        (a, b) => (b.rating_average || 0) - (a.rating_average || 0)
      );

      setNutritionists(sorted as Nutritionist[]);
    } catch (error) {
      console.error('Error loading nutritionists:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredNutritionists = nutritionists.filter((nutritionist) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      nutritionist.name.toLowerCase().includes(query) ||
      nutritionist.bio?.toLowerCase().includes(query) ||
      nutritionist.service_areas?.some((area) => area.toLowerCase().includes(query))
    );
  });

  if (loading) {
    return (
      <>
        <Header />
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
            <p className="mt-4 text-sm text-gray-600">Loading nutritionists...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="min-h-screen bg-gray-50 py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Find a Nutritionist</h1>
            <p className="mt-2 text-gray-600">
              Browse certified nutritionists and book personalized consultations
            </p>
          </div>

          {/* Search */}
          <div className="mb-8">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search nutritionists by name, specialty, or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white py-3 pl-10 pr-4 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
          </div>

          {/* Nutritionists Grid */}
          {filteredNutritionists.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-white p-12 text-center">
              <p className="text-lg font-semibold text-gray-900">No nutritionists found</p>
              <p className="mt-2 text-sm text-gray-600">Try adjusting your search</p>
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filteredNutritionists.map((nutritionist) => (
                <Link
                  key={nutritionist.id}
                  to={`/nutritionist/${nutritionist.id}`}
                  className="group rounded-xl border border-gray-200 bg-white p-6 shadow-sm transition hover:shadow-md"
                >
                  <div className="mb-4 flex items-start gap-4">
                    <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-full bg-gray-200">
                      {nutritionist.profile_image_url ? (
                        <img
                          src={nutritionist.profile_image_url}
                          alt={nutritionist.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-green-100 text-green-600">
                          <span className="text-xl font-semibold">
                            {nutritionist.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600">
                        {nutritionist.name}
                      </h3>
                      <div className="mt-1 flex items-center gap-2">
                        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                        <span className="text-sm font-semibold text-gray-700">
                          {nutritionist.rating_average?.toFixed(1) || '0.0'}
                        </span>
                        <span className="text-sm text-gray-500">
                          ({nutritionist.total_reviews || 0} reviews)
                        </span>
                      </div>
                    </div>
                  </div>
                  {nutritionist.bio && (
                    <p className="mb-4 line-clamp-2 text-sm text-gray-600">
                      {nutritionist.bio}
                    </p>
                  )}
                  {nutritionist.service_areas && nutritionist.service_areas.length > 0 && (
                    <div className="flex items-start gap-2 text-sm">
                      <MapPin className="h-4 w-4 flex-shrink-0 text-gray-400" />
                      <span className="text-gray-600">
                        {nutritionist.service_areas.slice(0, 2).join(', ')}
                        {nutritionist.service_areas.length > 2 && '...'}
                      </span>
                    </div>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
